import React from 'react';

import { ChartCard } from '@/design-system/chart-card';
import type { CoverageResponse } from '@/services/api/dashboard';

import { fmtInt, fmtPct } from './format';

const DAY_FILL: Record<string, string> = {
  OK: 'bg-gray-900',
  EMPTY: 'bg-gray-300',
  PARTIAL: 'bg-warning-base',
  FAILED: 'bg-error-base',
};

/**
 * Snapshot coverage strip — one tick per expected day. PARTIAL/FAILED days
 * are present but EXCLUDED from every aggregate (PRD R11), so they get
 * status color; missing days are hollow.
 */
const CoverageCard: React.FC<{
  coverage: CoverageResponse | null;
  loading: boolean;
}> = ({ coverage, loading }) => {
  const state = loading ? 'loading' : !coverage ? 'empty' : 'ready';

  return (
    <ChartCard
      eyebrow="Data quality"
      title="Snapshot coverage"
      subtitle={
        coverage
          ? `${fmtInt(coverage.presentDays)} of ${fmtInt(coverage.expectedDays)} days included` +
            (coverage.excludedDays > 0
              ? ` · ${coverage.excludedDays} excluded`
              : '')
          : undefined
      }
      value={coverage ? fmtPct(coverage.coverage) : undefined}
      delta={
        coverage?.lowCoverage
          ? { value: 'below threshold', trend: 'down' }
          : undefined
      }
      state={state}
      bodyClassName="px-4 pb-4"
    >
      {coverage && (
        <div>
          <div className="flex flex-wrap gap-1 pt-1">
            {coverage.days.map((d) => (
              <span
                key={d.date}
                title={`${d.date} · ${d.present ? (d.etlStatus ?? 'OK') : 'missing'}`}
                className={`h-4 w-2 rounded-[1px] ${
                  d.present
                    ? (DAY_FILL[d.etlStatus ?? 'OK'] ?? 'bg-gray-900')
                    : 'border border-stroke-soft-200 bg-transparent'
                }`}
              />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-text-sub-600">
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-[1px] bg-gray-900" /> OK
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-[1px] bg-gray-300" /> Empty day
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-[1px] bg-warning-base" /> Partial
              (excluded)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-[1px] bg-error-base" /> Failed
              (excluded)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-[1px] border border-stroke-soft-200" />{' '}
              Missing
            </span>
          </div>
        </div>
      )}
    </ChartCard>
  );
};

export default CoverageCard;
